import { PayloadAction, createSlice, nanoid } from '@reduxjs/toolkit';
import { clamp, cloneDeep, fromPairs } from 'lodash';

import canvasesAdapter from './canvases.adapter';
import actions from './canvases.actions';

import cartProductEditor from 'domains/cart_product_editor';
import optionGroups from 'domains/option_groups';
import svgs from 'domains/svgs';

import type { SavedArrangement } from 'domains/saved_arrangements';
import type {
  CanvasesSliceInitialState,
  CanvasConfiguration,
  CanvasMovementPayload,
} from './canvases.types';
import { hasMovementBounds, notNull } from 'utils/type_guards';
import { PickRequired, Point } from 'types/common_types';
import { calculateArrangementCanvasPositions } from 'utils/arrangement_util';

const initialState: CanvasesSliceInitialState = {
  active: null,
  activeLock: false,
  arrangementPosition: { x: 0, y: 0 },
  nextCanvasPosition: 0,
  status: 'idle',
};

const initialCanvasEntityState = {
  rotation: 0,
  userRotated: false,
  position: { x: 0, y: 0 },
};

const canvases = createSlice({
  name: 'canvases',
  initialState: canvasesAdapter.getInitialState(initialState),
  reducers: {
    addOne: canvasesAdapter.addOne,
    addCanvas: {
      prepare(payload: Partial<CanvasConfiguration>) {
        return {
          payload: { id: nanoid(), ...initialCanvasEntityState, ...payload },
        };
      },
      reducer(state, { payload }: PayloadAction<CanvasConfiguration>) {
        const update = canvasesAdapter.addOne(state, {
          ...payload,
          position: { x: state.nextCanvasPosition, y: 0 },
        });
        update.active = payload.id;

        return update;
      },
    },
    duplicateActiveCanvas: {
      prepare() {
        return { payload: { id: nanoid(), ...initialCanvasEntityState } };
      },
      reducer(state, { payload }: PayloadAction<CanvasConfiguration>) {
        if (state.active === null) return state;

        const copy = cloneDeep(state.entities[state.active]) as CanvasConfiguration;

        const newCanvas = { ...payload, ...copy };
        newCanvas.id = payload.id;
        newCanvas.position = { x: state.nextCanvasPosition, y: 0 };

        const update = canvasesAdapter.addOne(state, newCanvas);
        update.active = newCanvas.id;

        return update;
      },
    },
    updateCanvas(state, { payload }: PayloadAction<Partial<CanvasConfiguration>>) {
      const id = payload.id || state.active;
      if (!id) return state;

      return canvasesAdapter.updateOne(state, {
        id,
        changes: { ...payload },
      });
    },
    rotateCanvas: (state, { payload }: PayloadAction<string>) => {
      const canvas = state.entities[payload];

      // If trying to rotate a canvas with the wrong ID, do nothing
      if (!canvas) return state;

      const rotation = canvas.rotation === 90 ? 0 : 90;
      const update = canvasesAdapter.updateOne(state, {
        id: payload,
        changes: { rotation },
      });

      return update;
    },
    moveCanvas(state, { payload }: PayloadAction<PickRequired<CanvasMovementPayload, 'id'>>) {
      const { id, movementX, movementY } = payload;

      const canvas = state.entities[id];

      if (!canvas || !canvas.position) return state;

      let x = canvas.position.x + movementX;
      let y = canvas.position.y + movementY;

      if (hasMovementBounds(payload)) {
        const { leftLimit, rightLimit, bottomLimit, topLimit } = payload;
        x = clamp(x, leftLimit, rightLimit);
        y = clamp(y, topLimit, bottomLimit);
      }

      let update = canvasesAdapter.updateOne(state, { id, changes: { position: { x, y } } });
      const all = canvasesAdapter.getSelectors().selectAll(state);
      const { leftmost, topmost, updates } = calculateArrangementCanvasPositions(all);

      if (leftmost && topmost) {
        // Update the arrangement with the x, y point matching the leftmost & topmost canvases
        update.arrangementPosition.x += leftmost.position.x;
        update.arrangementPosition.y += topmost.position.y;

        update = canvasesAdapter.updateMany(update, updates);
      }
      return update;
    },
    removeCanvas(state, { payload }: PayloadAction<string>) {
      const { ids } = state;
      const active = payload;
      const activeIndex = ids.indexOf(active);
      const idToActivate = ids[activeIndex - 1] ? ids[activeIndex - 1] : ids[activeIndex + 1];

      let update = canvasesAdapter.removeOne(state, active);
      update.active = idToActivate as string;

      const all = canvasesAdapter.getSelectors().selectAll(state);
      const { leftmost, topmost, updates } = calculateArrangementCanvasPositions(all);

      if (leftmost && topmost) {
        // Update the arrangement with the x, y point matching the leftmost & topmost canvases
        update.arrangementPosition.x += leftmost.position.x;
        update.arrangementPosition.y += topmost.position.y;

        update = canvasesAdapter.updateMany(update, updates);
      }

      return update;
    },
    setActiveCanvasId(state, { payload }: PayloadAction<string>) {
      if (state.activeLock) return state;

      return {
        ...state,
        active: payload,
      };
    },
    resetToSingleCanvas(state, { payload }: PayloadAction<string | undefined | null>) {
      let update;

      if (payload) {
        const toRemove = state.ids.filter((id) => id !== payload);
        update = canvasesAdapter.removeMany(state, toRemove);
        // Resetting with an ID will maintain the existing product config, but we always want to
        // unset any cart product being edited
        update = canvasesAdapter.updateOne(update, {
          id: payload,
          changes: { cartProductId: undefined },
        });
      } else {
        // If called without an id to keep, we need to be adding a new canvas soon
        update = canvasesAdapter.removeAll(state);
        update.active = null;
      }

      return update;
    },
    restoreArrangement(state, { payload }: PayloadAction<SavedArrangement>) {
      const { canvases: arrangementCanvases } = payload;
      const canvasesToAdd = arrangementCanvases.map((canvas) => ({
        ...canvas.config,
        position: { x: canvas.x, y: canvas.y },
      }));

      const update = canvasesAdapter.addMany(
        canvasesAdapter.getInitialState(initialState),
        canvasesToAdd
      );

      update.active = canvasesToAdd[0].id;

      return update;
    },
    moveArrangement(state, { payload }: PayloadAction<CanvasMovementPayload>) {
      const { movementX, movementY } = payload;
      let x = state.arrangementPosition.x + movementX;
      let y = state.arrangementPosition.y + movementY;

      if (hasMovementBounds(payload)) {
        const { leftLimit, rightLimit, bottomLimit, topLimit } = payload;
        x = clamp(x, leftLimit, rightLimit);
        y = clamp(y, topLimit, bottomLimit);
      }

      return {
        ...state,
        arrangementPosition: { x, y },
      };
    },
    setArrangementPosition(state, { payload }: PayloadAction<Point>) {
      const { x, y } = payload;
      return {
        ...state,
        arrangementPosition: { x, y },
      };
    },

    setNextCanvasPosition(state, { payload }: PayloadAction<number>) {
      return {
        ...state,
        nextCanvasPosition: payload,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(actions.changeActiveCanvasConfig.catalog, (state, { payload }) => {
        if (!state.active) return state;

        const update = canvasesAdapter.updateOne(state, {
          id: state.active,
          changes: { catalog: payload },
        });

        update.activeLock = true;
        return update;
      })
      .addCase(actions.changeActiveCanvasConfig.subcatalog, (state, { payload }) => {
        if (!state.active) return state;

        const update = canvasesAdapter.updateOne(state, {
          id: state.active,
          changes: { subcatalog: payload },
        });

        update.activeLock = true;
        return update;
      })
      .addCase(actions.changeActiveCanvasConfig.product, (state, { payload }) => {
        if (!state.active) return state;

        const update = canvasesAdapter.updateOne(state, {
          id: state.active,
          changes: { product: payload },
        });

        // If the selected product is the custom size product, the pending price call will initialize this value
        update.priceFetchSuccessCount = undefined;

        update.activeLock = true;
        return update;
      })
      .addCase(actions.changeActiveCanvasConfig.options, (state, { payload }) => {
        if (!state.active) return state;
        const active = state.entities[state.active];
        if (!active) return state;

        let options;

        if (!payload.replace && active.options) {
          options = {
            ...active.options,
            ...fromPairs(payload.options),
          };
        } else {
          options = fromPairs(payload.options);
        }

        const update = canvasesAdapter.updateOne(state, {
          id: state.active,
          changes: {
            options,
          },
        });

        // When status is loading during an options update, that means we're updating due to a
        // custom size change, so don't unlock the active yet
        if (state.status !== 'loading') {
          update.activeLock = false;
        }

        return update;
      })
      .addCase(actions.changeActiveCanvasConfig.multi, (state, { payload }) => {
        if (!state.active) return state;

        const update = canvasesAdapter.updateOne(state, {
          id: state.active,
          changes: payload,
        });

        update.activeLock = true;
        return update;
      })
      .addCase(actions.changeActiveCanvasConfig.size, (state, { payload }) => {
        if (!state.active) return state;

        const update = canvasesAdapter.updateOne(state, {
          id: state.active,
          changes: {
            ...payload,
            rotation: 0,
          },
        });

        update.activeLock = true;
        return update;
      })
      .addCase(actions.changeActiveCanvasConfig.priceToolSize, (state, { payload }) => {
        if (!state.active) return;

        return canvasesAdapter.updateOne(state, {
          id: state.active,
          changes: {
            ...payload,
          },
        });
      })
      .addCase(optionGroups.actions.fetchOptionGroups.fulfilled, (state, { payload }) => {
        if (payload.optionGroups.length === 0) {
          state.activeLock = false;
        }

        return state;
      })
      .addCase(actions.calculateCustomPrice.pending, (state) => {
        return { ...state, status: 'loading', priceFetchSuccessCount: 0 };
      })
      .addCase(actions.calculateCustomPrice.fulfilled, (state, { payload }) => {
        if (state.active === null) return state;

        const update = canvasesAdapter.updateOne(state, {
          id: state.active,
          changes: { customPrice: payload },
        });

        if (notNull(update.priceFetchSuccessCount)) {
          update.priceFetchSuccessCount += 1;
        }

        if (update.priceFetchSuccessCount === 2) {
          update.status = 'idle';
          update.activeLock = false;
        }

        return update;
      })
      .addCase(actions.calculateCustomPrice.rejected, (state) => {
        if (state.active === null) return state;

        const update = canvasesAdapter.updateOne(state, {
          id: state.active,
          changes: { customPrice: undefined },
        });

        update.status = 'error';
        return update;
      })
      .addCase(actions.calculateCustomOptionPrices.fulfilled, (state, { payload }) => {
        if (state.active === null) return state;

        const customOptionPrices = fromPairs(
          payload.map(([ogid, cost]) => [ogid, cost.toFixed(2)])
        );

        const update = canvasesAdapter.updateOne(state, {
          id: state.active,
          changes: { customOptionPrices },
        });

        if (notNull(update.priceFetchSuccessCount)) {
          update.priceFetchSuccessCount += 1;
        }

        if (update.priceFetchSuccessCount === 2) {
          update.status = 'idle';
          update.activeLock = false;
        }

        return update;
      })
      .addCase(actions.calculateCustomOptionPrices.rejected, (state) => {
        if (state.active === null) return state;

        const update = canvasesAdapter.updateOne(state, {
          id: state.active,
          changes: { customOptionPrices: undefined },
        });

        update.status = 'error';
        return update;
      })
      .addCase(svgs.actions.getSVGImage.fulfilled, (state, { payload }) => {
        if (state.active === null || !payload.hasBorderColor || !payload.hideBorderColor)
          return state;

        // In the special case where the data-border-color is set to false,
        // we need to set the border color to white, but _not_ show the border color picker
        if (payload.hideBorderColor) {
          return canvasesAdapter.updateOne(state, {
            id: state.active,
            changes: { borderColor: 'FFFFFFFF' },
          });
        }

        if (payload.hasBorderColor) {
          return canvasesAdapter.updateOne(state, {
            id: state.active,
            changes: { borderColor: state.entities[state.active]?.borderColor || 'FFFFFFFF' },
          });
        }
      })

      .addCase(cartProductEditor.actions.pending, (state) => {
        return canvasesAdapter.removeAll(state);
      })
      .addCase(cartProductEditor.actions.canvasReady, (state, { payload }) => {
        const update = canvasesAdapter.addOne(state, {
          ...payload,
          position: { x: state.nextCanvasPosition, y: 0 },
        });
        update.active = payload.id;
        return update;
      });
  },
});

export default canvases;
